import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fmtTons, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { FIELD, keepVisible } from "./imageUtils";

const SaleChoice = ({ row, saleId, setSaleId }) => (
  row.web_sales.length > 1 ? (
    <div className="space-y-1">
      <div className="text-sm font-medium">Choose the exact website sale:</div>
      {row.web_sales.map((s) => (
        <label key={s.id} className="flex items-center gap-2 text-sm p-2 border rounded-md bg-white">
          <input type="radio" name={`sale-${row.key}`} data-testid={`pick-sale-${s.id}`} checked={saleId === s.id} onChange={() => setSaleId(s.id)} className="h-5 w-5" />
          {fmtTons(s.tons)} t · {s.vehicle_no || "no vehicle"} · entered by {s.created_by_name || "?"}
        </label>
      ))}
    </div>
  ) : null
);

// Inline approval panel for one discrepancy. Website changes happen only after the explicit approve button.
export const CorrectionPanel = ({ sessionId, plant, row, onDone, onCancel }) => {
  const [saleId, setSaleId] = useState(row.web_sales.length === 1 ? row.web_sales[0].id : "");
  const [vehicle, setVehicle] = useState("");
  const [sure, setSure] = useState(false);
  const [busy, setBusy] = useState(false);
  const sale = row.web_sales.find((s) => s.id === saleId);
  const others = row.web_sales.filter((s) => s.id !== saleId).reduce((a, s) => a + s.tons, 0);
  const newTons = sale ? Math.round((row.book_tons - others) * 1000) / 1000 : null;

  const send = async (action, extra = {}) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/verification/sessions/${sessionId}/decisions`, { plant, row_key: row.key, action, ...extra });
      toast.success(data.duplicate ? "Already applied earlier" : "Correction saved");
      if (data.billed_warning) toast.warning("A bill is already saved for this customer and month. Open Monthly Bill and save it again.", { duration: 9000 });
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); onDone(); }
    finally { setBusy(false); }
  };

  const Warn = () => row.billed ? <p className="text-sm text-amber-800 flex gap-1"><AlertTriangle className="h-4 w-4 shrink-0" />A bill is already saved for this month. After this change, re-save the bill in Monthly Bill.</p> : null;
  const Btn = ({ children, tid, ...p }) => <Button type="button" className="h-11" data-testid={tid} disabled={busy} {...p}>{children}</Button>;

  return (
    <div data-testid={`correction-panel-${row.key}`} className="bg-slate-50 border-2 border-slate-300 rounded-xl p-3 space-y-3 mt-2">
      {row.status === "mismatch" && (<>
        <div className="text-sm">Notebook: <b>{fmtTons(row.book_tons)} t</b> · Website: <b>{fmtTons(row.web_tons)} t</b> · Difference <b className="text-red-600">{row.diff > 0 ? "+" : ""}{row.diff.toFixed(3)} t</b></div>
        <SaleChoice row={row} saleId={saleId} setSaleId={setSaleId} />
        {sale && <div data-testid="correction-preview" className="text-sm bg-white border rounded-md p-2">Website sale {fmtDate(sale.sale_date)} · {sale.customer_name} · {sale.plant}: <span className="line-through text-red-700">{fmtTons(sale.tons)} t</span> → <b className="text-emerald-700">{fmtTons(newTons)} t</b></div>}
        <Warn />
        <div className="flex flex-wrap gap-2">
          <Btn tid="approve-update-sale" className="h-11 bg-emerald-600 hover:bg-emerald-700" disabled={busy || !sale || newTons <= 0} onClick={() => send("update_sale", { sale_id: saleId })}>Notebook is right — approve website update</Btn>
          <Btn tid="accept-website" variant="outline" onClick={() => send("accept_website")}>Website is right — notebook was wrong</Btn>
        </div>
      </>)}
      {row.status === "missing_on_website" && (<>
        <div data-testid="create-preview" className="text-sm bg-white border rounded-md p-2">New website sale: <b>{fmtDate(row.date)}</b> · <b>{row.customer}</b> · <b>{fmtTons(row.book_tons)} t</b> · {plant}</div>
        <label className="block text-sm">Vehicle No (optional)<Input className={FIELD} data-testid="create-vehicle" value={vehicle} onFocus={keepVisible} autoCapitalize="characters" onChange={(e) => setVehicle(e.target.value)} /></label>
        <Warn />
        <div className="flex flex-wrap gap-2">
          <Btn tid="approve-create-sale" className="h-11 bg-emerald-600 hover:bg-emerald-700" onClick={() => send("create_sale", { vehicle_no: vehicle })}>Approve — save this sale on website</Btn>
          <Btn tid="dismiss-create" variant="outline" onClick={() => send("dismiss")}>Don't save (notebook was wrong)</Btn>
        </div>
      </>)}
      {row.status === "missing_in_book" && (<>
        <p className="text-sm">This website sale was <b>not found in the notebook</b>. Was it genuinely missing from the notebook, or is the website sale wrong?</p>
        <SaleChoice row={row} saleId={saleId} setSaleId={setSaleId} />
        <div className="flex flex-wrap gap-2">
          <Btn tid="confirm-valid" className="h-11 bg-emerald-600 hover:bg-emerald-700" onClick={() => send("confirm_valid")}>Confirm sale is valid</Btn>
          <Btn tid="confirm-incorrect" variant="outline" className="h-11 text-red-700 border-red-300" disabled={busy || !sale} onClick={() => setSure(true)}>Confirm sale is incorrect</Btn>
        </div>
        {sure && sale && (
          <div data-testid="delete-confirm" className="bg-red-50 border border-red-300 rounded-md p-3 space-y-2">
            <p className="text-sm text-red-800">Delete website sale {fmtDate(sale.sale_date)} · {sale.customer_name} · {fmtTons(sale.tons)} t · {sale.plant}? This is recorded in the audit history.</p>
            <Warn />
            <div className="flex gap-2"><Btn tid="delete-sale-yes" className="h-11 bg-red-600 hover:bg-red-700" onClick={() => send("delete_sale", { sale_id: saleId })}>Yes, delete this sale</Btn>
              <Btn tid="delete-sale-no" variant="outline" onClick={() => setSure(false)}>No</Btn></div>
          </div>
        )}
      </>)}
      <Button type="button" variant="ghost" className="h-10" data-testid="correction-cancel" onClick={onCancel}>Cancel</Button>
    </div>
  );
};
